import {
  AlertTriangle,
  ArrowDownToLine,
  CheckCircle2,
  PackageCheck,
  X,
} from 'lucide-react';
import type { ProductionExecutionResult } from '../../../types/restaurant/RestaurantProductionPlannerTypes';
import { formatDecimal, formatMoney } from '../../../utils/decimalValue';

const ProductionResultPanel = ({
  result,
  onClose,
}: {
  result: ProductionExecutionResult;
  onClose: () => void;
}) => (
  <section
    data-testid="production-result"
    className="rounded-xl border border-success/30 bg-success/5 p-5"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex gap-3">
        <span className="rounded-full bg-success/10 p-3 text-success">
          <CheckCircle2 className="h-6 w-6" />
        </span>
        <div>
          <h2 className="text-lg font-semibold text-black dark:text-white">
            Lote #{result.batch.id} completado
          </h2>
          <p className="text-sm text-bodydark2">
            {result.batch.items.length} producto(s) ingresados · costo{' '}
            {formatMoney(result.batch.totalCost)}
          </p>
        </div>
      </div>
      <button
        onClick={onClose}
        aria-label="Cerrar resultado"
        className="rounded p-2 hover:bg-success/10"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
    <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="rounded-lg border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
        <h3 className="mb-3 flex items-center gap-2 font-semibold text-black dark:text-white">
          <PackageCheck className="h-5 w-5 text-success" />
          Productos terminados
        </h3>
        {result.batch.items.map((item) => (
          <div
            key={item.id}
            className="flex justify-between border-t border-stroke py-2 text-sm first:border-0 dark:border-strokedark"
          >
            <span>
              {item.productName} · {formatDecimal(item.quantity)}
            </span>
            <strong>{formatMoney(item.totalCost)}</strong>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
        <h3 className="mb-3 flex items-center gap-2 font-semibold text-black dark:text-white">
          <ArrowDownToLine className="h-5 w-5 text-primary" />
          Movimientos generados
        </h3>
        <p className="text-sm">
          Materia prima: <strong>{result.rawMaterialMovements.length}</strong>
        </p>
        <p className="mt-1 text-sm">
          Producto terminado: <strong>{result.productMovements.length}</strong>
        </p>
      </div>
    </div>
    {result.lowStockAlerts.length > 0 && (
      <div className="mt-4 rounded-lg border border-warning/20 bg-warning/10 p-4 text-warning">
        <h3 className="flex items-center gap-2 font-semibold">
          <AlertTriangle className="h-5 w-5" />
          Alertas de stock bajo
        </h3>
        <ul className="mt-2 list-inside list-disc text-sm">
          {result.lowStockAlerts.map((alert) => (
            <li key={alert.rawMaterialId}>
              {alert.rawMaterialCode} · {alert.rawMaterialName}:{' '}
              {formatDecimal(alert.currentStock)} {alert.unitCode} (mínimo{' '}
              {formatDecimal(alert.minimumStock)})
            </li>
          ))}
        </ul>
      </div>
    )}
  </section>
);

export default ProductionResultPanel;
